import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service, TeamMember } from 'webapp-types';
import type { TeamType } from 'webapp/enums/enums';
import { TeamMemberStatus } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { equal } from 'webapp/utils/common';

import eq from 'webapp/helpers/eq';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormColorPickerComponent from 'webapp/components/form/color-picker';
import FormInputComponent from 'webapp/components/form/input';
import FormRadiobuttonSingleComponent from 'webapp/components/form/radiobutton-single';
import FormSelectComponent from 'webapp/components/form/select';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';
import TeamsTextComponent from 'webapp/components/teams/text';

import type Team from 'webapp/models-custom/team';

import type { UserDataForList } from 'webapp/services/model-manager/users';

const CustomColorId = 'custom';

interface TeamSnapshot {
	name: string | null;
	type: TeamType;
	color: string;
	description: string | null;
	ownerId: number | null;
	teamMembers: TeamMember[];
}

export default class TeamPopupEditComponent extends PopupInnerModalComponent<PopupData.Teams.Edit> {
	@service('team') declare teamService: Service.Team;
	@service('model-manager/teams') declare mmTeamsService: Service.ModelManager.Teams;
	@service('request') declare requestService: Service.Request;
	@service('constants') declare constantsService: Service.Constants;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('popup') declare popupService: Service.Popup;

	@tracked override submitInProgress = false;
	@tracked selectedMembers: string[];
	@tracked selectedColor: string;
	@tracked teamName: string;
	@tracked lastUsedTeamName = '';
	@tracked currentOwnerId: number | null = null;

	currentDescription: string | null = null;
	currentName: string | null = null;
	currentType: TeamType | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const { name, type, color, description, ownerId, teamMembers } = this.model;

		this._team = {
			name,
			type,
			color,
			description,
			ownerId,
			teamMembers,
		};

		this.teamName = name;
		this.selectedColor = color;

		this.currentDescription = description;
		this.currentOwnerId = ownerId;
		this.currentName = name;
		this.currentType = type;

		if (!this.availableColors.find((i) => i.value === color)) {
			this.selectedColor = CustomColorId;
		}

		this.selectedMembers = this.memberIds;

		if (this.isNew) {
			this.analyticsService.event(TrackingEvent.TEAM_CREATE_POPUP_OPENED);
		} else {
			this.analyticsService.event(TrackingEvent.TEAM_EDIT_POPUP_OPENED);
		}
	}

	get model(): Team {
		return this.popupData.model;
	}

	get type(): TeamType {
		return this.model.type;
	}

	get isCustomColorSelected(): boolean {
		return this.selectedColor === CustomColorId;
	}

	get ownerId(): number | null {
		return this.model.ownerId;
	}

	get members(): TeamMember[] {
		return this.model.teamMembers;
	}

	get memberIds(): string[] {
		return this.members.map((member) => String(member.id));
	}

	get stringifiedOwnerId(): string | null {
		const ownerId = this.model.stringifiedOwnerId;
		return ownerId.length ? ownerId : null;
	}

	get usersList(): UserDataForList[] {
		return this.mmUsersService.activatedAndVerifiedUsersWithoutViewersList;
	}

	get description(): string {
		return this.model.description ?? '';
	}

	get normalizedUsers() {
		const ownerId = this.stringifiedOwnerId;

		return this.usersList
			.filter((u) => u.value !== ownerId)
			.map((user: UserDataForList) => {
				return {
					value: user.value,
					label: user.label,
					label2: user.label2,
					disabled: user.value === ownerId,
				};
			});
	}

	get isTeamNameDuplicated(): boolean {
		const lowercasedName = this.model.name?.toLowerCase(),
			id = this.model.id;

		return this.mmTeamsService.teamModels
			.filter((team) => team.id !== id)
			.some((team) => team.name.toLowerCase() === lowercasedName);
	}

	get isNew(): boolean {
		return this.model.isNew;
	}

	get canEditVisibility(): boolean {
		return !(this.isNew || this.model.isOwnedByMe);
	}

	get isSaveDisabled(): boolean {
		return (
			!this.teamName ||
			this.isTeamNameDuplicated ||
			this.submitInProgress ||
			!this.hasChanges ||
			!this.ownerId
		);
	}

	get hasChanges(): boolean {
		if (!this._team || !this.model) {
			return false;
		}

		const hasNameChange = this._team.name !== this.model.name;
		const hasColorChange = this._team.color !== this.model.color;
		const hasTypeChange = this._team.type !== this.model.type;
		const hasDescriptionChange = this._team.description !== this.model.description;
		const hasOwnerChange = this._team.ownerId !== this.model.ownerId;

		return (
			hasNameChange ||
			hasColorChange ||
			hasTypeChange ||
			hasDescriptionChange ||
			hasOwnerChange ||
			this.hasMembersChange
		);
	}

	get hasMembersChange(): boolean {
		return !equal(this.memberIds.sort(), this.selectedMembers.sort());
	}

	get availableColors() {
		const colors = this.constantsService.boardColors.map((colorData) => {
			return {
				value: colorData.hex,
				label: colorData.name,
				colordroplet: colorData.hex,
			};
		});

		colors.push({
			value: CustomColorId,
			label: 'Custom',
			colordroplet: 'stripes',
		});

		return colors;
	}

	get popupTitle(): string {
		const postfix = 'space';
		return this.isNew ? `Create a new ${postfix}` : `Edit ${postfix}`;
	}

	@action
	onOwnerChange(ownerId: string | null): void {
		this.model.ownerId = ownerId ? Number(ownerId) : null;
	}

	@action
	updateTitle(): void {
		const trimmedName = this.teamName?.trim();

		this.model.name = trimmedName;
		this.teamName = trimmedName;
	}

	@action
	changeColor(color: string): void {
		this.selectedColor = color;

		if (!this.isCustomColorSelected) {
			this.model.color = color;
		}
	}

	@action
	changeCustomColor(color: string): void {
		this.model.color = color;
	}

	@action
	onMembersChange(memberIds: string[]): void {
		this.selectedMembers = this.stringifiedOwnerId
			? [...memberIds, this.stringifiedOwnerId]
			: [...memberIds];

		this.scheduleReposition();
	}

	@action
	onTypeChange(type: AnySimpleValue): void {
		this.model.type = type as TeamType;
	}

	@action
	onDescriptionChange(text: string): void {
		this.model.description = text.length ? text.trim() : null;
	}

	@action
	async save(): Promise<void> {
		if (!this.model || this.submitInProgress) {
			return;
		}

		const isNew = this.isNew;

		if (!isNew) {
			const canProceed = await this.confirmMembersRemoval();

			if (!canProceed) {
				return;
			}
		}

		this.submitInProgress = true;

		// we track it before saving in case of error
		if (isNew) {
			this.analyticsService.event(TrackingEvent.TEAM_CREATED, {
				type: this.model.teamTypeForTracking,
			});
		} else {
			const { description, ownerId, name, type } = this.model;

			const action = [];
			if (this.currentDescription?.trim() !== description?.trim()) {
				action.push('description');
			}

			if (this.currentOwnerId !== ownerId) {
				action.push('owner');
			}

			if (this.currentName?.trim() !== name?.trim()) {
				action.push('name');
			}

			if (this.currentType !== type) {
				action.push('type');
			}

			if (this.hasMembersChange) {
				action.push('members');
			}

			if (action.length) {
				this.analyticsService.event(TrackingEvent.TEAM_PAGE_EDITED, {
					action,
				});
			}
		}

		this.model.teamMembers = this.selectedMembers.map((id) => ({
			id: Number(id),
			status: TeamMemberStatus.MEMBER,
		}));

		this.model
			.save()
			.then((item) => {
				if (isNew) {
					this.navigationService.replaceRoute(`/teams/${item.id}`);
				}

				this.infolayerService.ok(
					`Space has been successfully ${isNew ? 'created' : 'updated'}`,
				);

				this.close();
			})
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	closePopup(): void {
		if (!this.submitInProgress) {
			this.close();
		}
	}

	private _team: TeamSnapshot;

	private async confirmMembersRemoval(): Promise<boolean> {
		const newMembers = this.model.teamMembers;
		const currentMembers = this._team.teamMembers;

		const removedMembers = currentMembers.filter(
			(member) => !newMembers.some((newMember) => newMember.id === member.id),
		);

		if (removedMembers.length === 0) {
			return true;
		}

		const removedMemberNames = removedMembers
			.map((member) => {
				const user = this.usersList.find((u) => Number(u.value) === member.id);
				return user?.label;
			})
			.join(', ');

		const memberCount = removedMembers.length;
		const title =
			memberCount === 1
				? `Remove user from the "${this.model.name}" space?`
				: `Remove ${memberCount} users from the "${this.model.name}" space?`;

		const text = `The following ${memberCount === 1 ? 'user' : 'users'} will lose access to all space-related content:\n${removedMemberNames}`;

		return new Promise<boolean>((resolve) => {
			this.popupService.openConfirmPopup({
				onConfirm: () => {
					resolve(true);
				},
				onReject: () => {
					this.model.teamMembers = [...currentMembers];
					this.selectedMembers = currentMembers.map((member) => String(member.id));
					resolve(false);
				},
				title,
				text,
				confirmAction: 'Remove from space',
				cancelAction: 'Cancel',
			});
		});
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Spaces" @size="lg" />
			{{this.popupTitle}}
		</div>

		<div class="content">
			<div class="row input">
				<label>Space name</label>
				<div class="right">
					<FormInputComponent
						@value={{this.teamName}}
						placeholder="Enter space name (e.g. Marketing, Product, Sales, Finance, etc.)"
						@hideFloatLabel={{true}}
						@autofocus={{this.model.isNew}}
						disabled={{this.submitInProgress}}
						@focusOut={{this.updateTitle}}
						maxlength="50"
						required={{true}}
					/>
					{{#if this.isTeamNameDuplicated}}
						<div class="error">Space name is already taken</div>
					{{/if}}
				</div>
			</div>

			<div class="row input">
				<label>Description <span class="optional">(optional)</span></label>
				<div class="right teams-html-editor">
					<TeamsTextComponent
						@placeholder="Add description"
						@text={{this.description}}
						@onChange={{this.onDescriptionChange}}
					/>
				</div>
			</div>

			<div class="row input">
				<label>Owner</label>
				<div class="right">
					<FormSelectComponent
						name="ownerId"
						@selected={{this.stringifiedOwnerId}}
						@options={{this.usersList}}
						@onChange={{this.onOwnerChange}}
						@placeholder="Select owner"
						@showFloatlabel={{false}}
						@isDisabled={{this.submitInProgress}}
						required={{true}}
						@isNumeric={{false}}
					/>
				</div>
			</div>

			<div class="row input">
				<label>Member(s)</label>
				<div class="right recipients">
					<FormSelectComponent
						name="teamMembers"
						@selected={{this.selectedMembers}}
						@options={{this.normalizedUsers}}
						@onChange={{this.onMembersChange}}
						@placeholder="Select members"
						@showFloatlabel={{false}}
						@isDisabled={{this.submitInProgress}}
						@isMultiple={{true}}
						@isNumeric={{false}}
						@isRequired={{true}}
					/>
				</div>
			</div>

			<div class="row input">
				<label>Background color</label>
				<div class="right color-select">
					<FormSelectComponent
						name="color"
						@options={{this.availableColors}}
						@selected={{this.selectedColor}}
						@isNumeric={{false}}
						@isRequired={{true}}
						@onChange={{this.changeColor}}
					/>
				</div>
				{{#if this.isCustomColorSelected}}
					<div class="customcolor">
						<FormColorPickerComponent
							@value={{this.model.color}}
							@colorIcon="raindrop"
							@onChange={{this.changeCustomColor}}
						/>
					</div>
				{{/if}}
			</div>

			<div class="row">
				<label>Type</label>
				<div class="right radiobutton-default radiobutton-group size-normal">
					<span class="radiobutton size-normal val-default">
						<FormRadiobuttonSingleComponent
							@id="id-open"
							@name="team-type"
							@value={{1}}
							@isDisabled={{this.canEditVisibility}}
							@selected={{this.type}}
							@onChange={{this.onTypeChange}}
						/>
						<label for="id-open">Open</label>
					</span>
					<div class="description">
						Anyone can find, view, and join the space without approval.
					</div>

					<span class="radiobutton size-normal val-default">
						<FormRadiobuttonSingleComponent
							@id="id-closed"
							@name="team-type"
							@value={{2}}
							@selected={{this.type}}
							@isDisabled={{this.canEditVisibility}}
							@onChange={{this.onTypeChange}}
						/>
						<label for="id-closed">
							Closed
							<CommonUiIconComponent @name="Lock" @size="sm" />
						</label>
					</span>
					<div class="description">
						Anyone can find the space, but only approved members can view content.
					</div>

					<span class="radiobutton size-normal val-default radio-input">
						<FormRadiobuttonSingleComponent
							@id="id-private"
							@name="team-type"
							@value={{3}}
							@selected={{this.type}}
							@isDisabled={{this.canEditVisibility}}
							@onChange={{this.onTypeChange}}
						/>
						<label for="id-private">
							Private
							<CommonUiIconComponent @name="EyeClosed" @size="sm" @class="private" />
							{{#if (eq this.type 3)}}
								<span class="private-alert">
									<CommonUiIconComponent @name="AlertFill" @size="sm" />
									Non-members will not be able to find the space.
								</span>
							{{/if}}
						</label>
					</span>
					<div class="description">Only invited members can find, view, or join the space.</div>
				</div>
			</div>
			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text={{if this.model.isNew "Create" "Save"}}
						@extraClass="save"
						@onTrigger={{this.save}}
						@submitInProgress={{this.submitInProgress}}
						@isDisabled={{this.isSaveDisabled}}
					/>
				</div>
			</div>

		</div>
	</template>
}
